import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { BACKEND_URL, getErrorMessage } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { SessionFeedbackForm } from './session/SessionFeedbackForm';
import { SessionImageLibrary } from './session/SessionImageLibrary';
import { SessionFullScreen } from './session/SessionFullScreen';
import { SessionSkillGrid, getSkillImageSrc, SKILL_IMAGE_LIBRARY } from './session/SessionSkillGrid';
import { SessionVideoActions, SessionComfortRating } from './session/SessionVideoActions';
import { SessionTimer } from '@/components/SessionTimer';
import {
  Star, Play, Trash2, Award, Waves, Square, CheckCircle2,
  X, Download, Maximize2, Loader2, Edit3, Lock, Save, ArrowRight, ImagePlus
} from 'lucide-react';

export default function SessionPage() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isParent = user?.role === 'parent';
  const [session, setSession] = useState(null);
  const [activeSkill, setActiveSkill] = useState(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showEndSession, setShowEndSession] = useState(false);
  const [instructorNotes, setInstructorNotes] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sessionFeedback, setSessionFeedback] = useState({
    effective_strategies: '', recommendations_next: '', thinking_moment: '',
    reasoning_strategy: '', parent_note: '', additional_observations: '',
    instructor_name: '', date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
  });
  const [parentFeedback, setParentFeedback] = useState([]);
  const [fullScreenMode, setFullScreenMode] = useState(false);
  const [fullscreenSkill, setFullscreenSkill] = useState(null);
  const [showImageLibrary, setShowImageLibrary] = useState(null);
  const [customLibrary, setCustomLibrary] = useState([]);
  const [savingToLibrary, setSavingToLibrary] = useState(false);
  const [uploadingToLibrary, setUploadingToLibrary] = useState(false);
  const [newImageName, setNewImageName] = useState('');
  const [comfortRatings, setComfortRatings] = useState({});

  const fetchSession = useCallback(async () => {
    try {
      const res = await api.get(`/sessions/${sessionId}`);
      setSession(res.data);
      if (res.data.skills?.length > 0 && !activeSkill) {
        const firstIncomplete = res.data.skills.find(s => !s.completed);
        setActiveSkill(firstIncomplete || res.data.skills[0]);
      }
      if (res.data.status === 'completed' && res.data.child_id) {
        try {
          const fbRes = await api.get(`/feedback?child_id=${res.data.child_id}`);
          setParentFeedback(fbRes.data.filter(f => f.session_id === sessionId));
        } catch { /* silent */ }
      }
    } catch { toast.error('Failed to load session'); }
  }, [sessionId, activeSkill]);

  useEffect(() => { fetchSession(); }, [fetchSession]);
  useEffect(() => { if (user?.name) setSessionFeedback(prev => ({ ...prev, instructor_name: user.name })); }, [user]);
  useEffect(() => {
    const fetchLibrary = async () => {
      try { const res = await api.get('/skill-images/library'); setCustomLibrary(res.data); } catch { /* silent */ }
    };
    fetchLibrary();
  }, []);

  const startSession = async () => {
    await api.put(`/sessions/${sessionId}`, { status: 'active' });
    fetchSession();
    toast.success('Session started!');
  };

  const endSession = async () => {
    const missingComfort = session.skills.filter(s => !comfortRatings[s.skill_id]);
    if (missingComfort.length > 0) {
      toast.error(`Please rate how comfortable the child was for all skills. Missing: ${missingComfort.map(s => s.name).join(', ')}`);
      return;
    }
    const feedback = { ...sessionFeedback };
    if (!feedback.instructor_name && user?.name) feedback.instructor_name = user.name;
    await api.put(`/sessions/${sessionId}`, { status: 'completed', instructor_notes: instructorNotes, session_feedback: feedback });
    toast.success('Session completed! Redirecting to assessment...');
    setShowEndSession(false);
    navigate(`/dashboard/assessment/${sessionId}`);
  };

  const discardSession = async () => {
    try {
      await api.delete(`/sessions/${sessionId}/discard`);
      toast.success('Session discarded');
      navigate('/dashboard');
    } catch (err) { toast.error(getErrorMessage(err, 'Cannot discard. Contact admin to delete completed sessions.')); }
    setShowDiscardConfirm(false);
  };

  const downloadSessionPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/sessions/${sessionId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `SplashAble_Session_${session.session_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Session report PDF downloaded!');
    } catch { toast.error('Failed to download PDF'); }
    finally { setDownloadingPdf(false); }
  };

  const handleFullScreenComplete = async (skillId) => {
    try { await api.put(`/sessions/${sessionId}/skills/${skillId}/complete`); await fetchSession(); }
    catch { toast.error('Failed to complete skill'); }
  };

  const uncompleteSkill = async (skill) => {
    try { await api.put(`/sessions/${sessionId}/skills/${skill.skill_id}/uncomplete`); toast.success(`${skill.name} marked as incomplete`); fetchSession(); }
    catch { toast.error('Failed to undo'); }
  };

  const handlePresetImage = async (skillId, imageUrl) => {
    if (!session) return;
    const updated = session.skills.map(s => s.skill_id === skillId ? { ...s, image: imageUrl, image_is_url: true } : s);
    await api.put(`/sessions/${sessionId}`, { skills: updated });
    fetchSession();
    setShowImageLibrary(null);
    toast.success('Image added!');
  };

  const savePresetToLibrary = async (name, url) => {
    setSavingToLibrary(true);
    try { const res = await api.post('/skill-images/library', { image_url: url, name, is_url: true }); setCustomLibrary(prev => [res.data, ...prev]); toast.success('Saved to your library!'); }
    catch { toast.error('Failed to save'); }
    finally { setSavingToLibrary(false); }
  };

  const uploadToLibrary = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingToLibrary(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', newImageName || file.name.split('.')[0]);
      const res = await api.post('/skill-images/upload-to-library', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCustomLibrary(prev => [res.data, ...prev]);
      setNewImageName('');
      toast.success('Uploaded to your library!');
    } catch { toast.error('Failed to upload'); }
    finally { setUploadingToLibrary(false); }
  };

  const deleteFromLibrary = async (entryId) => {
    try { await api.delete(`/skill-images/library/${entryId}`); setCustomLibrary(prev => prev.filter(i => i.entry_id !== entryId)); toast.success('Removed from library'); }
    catch { toast.error('Failed to remove'); }
  };

  const getLibraryImageSrc = (img) => {
    if (img.is_url || img.image_url.startsWith('http')) return img.image_url;
    return `${BACKEND_URL}/api/uploads/${img.image_url}`;
  };

  const handleComfortRate = async (skillId, rating) => {
    setComfortRatings(prev => ({ ...prev, [skillId]: rating }));
    try {
      await api.post('/comfort-ratings', { session_id: sessionId, child_id: session.child_id, skill_id: skillId, rating, rated_by: 'instructor' });
    } catch { /* silent */ }
  };

  if (!session) return <div className="flex items-center justify-center p-12"><Waves className="w-10 h-10 text-[#22D3EE] animate-pulse" /></div>;

  const completedCount = session.skills.filter(s => s.completed).length;
  const allComplete = completedCount === session.skills.length && session.skills.length > 0;

  if (fullScreenMode && session.status === 'active') {
    return <SessionFullScreen session={session} onComplete={handleFullScreenComplete} onUncomplete={uncompleteSkill} onExit={() => setFullScreenMode(false)} />;
  }

  return (
    <div className="max-w-5xl mx-auto animate-fadeIn" data-testid="session-page">
      {/* Parent view-only banner */}
      {isParent && session.status === 'active' && (
        <div className="bg-[#ECFEFF] border border-[#22D3EE]/30 rounded-2xl p-4 mb-6 text-center" data-testid="parent-view-only-banner">
          <p className="text-sm font-semibold text-[#0E7490]">You're viewing this session live. Only the instructor can make changes.</p>
        </div>
      )}

      {/* Header with Timer */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>
            Session #{session.session_number}
          </h1>
          <div className="text-slate-500 mt-1">
            {completedCount}/{session.skills.length} skills completed
            <Badge variant="outline" className={`ml-2 rounded-full ${session.status === 'active' ? 'border-green-200 text-green-600 bg-green-50' : session.status === 'completed' ? 'border-blue-200 text-blue-600 bg-blue-50' : 'border-amber-200 text-amber-600 bg-amber-50'}`}>
              {session.status}
            </Badge>
          </div>
          {/* Session Timer — shown when active */}
          {session.status === 'active' && session.started_at && (
            <div className="mt-3">
              <SessionTimer startedAt={session.started_at} />
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isParent && session.status === 'active' && (
            <Button data-testid="fullscreen-btn" onClick={() => setFullScreenMode(true)} variant="outline" className="rounded-full font-bold gap-2 border-[#22D3EE] text-[#0E7490] h-12 px-5">
              <Maximize2 className="w-5 h-5" /> Visual Schedule
            </Button>
          )}
          {!isParent && session.status === 'planned' && (
            <>
              <Button data-testid="start-session-btn" onClick={startSession} className="rounded-full bg-green-500 hover:bg-green-600 text-white font-bold gap-2 h-12 px-5">
                <Play className="w-5 h-5" /> Start Session
              </Button>
              <Button data-testid="discard-session-btn" onClick={() => setShowDiscardConfirm(true)} variant="outline" className="rounded-full border-red-200 text-red-500 hover:bg-red-50 font-bold gap-2 h-12 px-5">
                <Trash2 className="w-5 h-5" /> Discard
              </Button>
            </>
          )}
          {!isParent && session.status === 'active' && (
            <>
              <Button data-testid="end-session-btn" onClick={() => setShowEndSession(true)} className="rounded-full bg-red-500 hover:bg-red-600 text-white font-bold gap-2 h-12 px-5">
                <Square className="w-5 h-5" /> End & Save
              </Button>
              <Button data-testid="discard-session-btn" onClick={() => setShowDiscardConfirm(true)} variant="outline" className="rounded-full border-red-200 text-red-500 hover:bg-red-50 font-bold gap-2 h-12 px-5">
                <Trash2 className="w-5 h-5" /> Discard
              </Button>
            </>
          )}
          {session.status === 'completed' && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button data-testid="download-session-pdf-btn" onClick={downloadSessionPdf} disabled={downloadingPdf} variant="outline" className="rounded-full border-2 border-[#22D3EE] text-[#0E7490] hover:bg-[#ECFEFF] font-bold gap-2 h-12 px-5">
                <Download className="w-5 h-5" /> {downloadingPdf ? 'Generating...' : 'Download PDF'}
              </Button>
              <div className="text-xs text-slate-400 bg-slate-50 rounded-full px-4 py-2">Completed</div>
            </div>
          )}
        </div>
      </div>

      {/* If/Then Reward */}
      {(session.if_condition || session.then_reward || (user?.role === 'instructor' && session.status !== 'completed')) && (
        <RewardEditor session={session} isInstructor={user?.role === 'instructor'} allComplete={allComplete} onUpdate={fetchSession} />
      )}

      {/* Instructor Notes & Feedback (completed sessions) */}
      {session.status === 'completed' && (session.session_feedback || session.instructor_notes) && (
        <Card className="rounded-3xl border-slate-100 shadow-sm mb-6" data-testid="session-instructor-notes">
          <CardContent className="p-6">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">Instructor Notes & Signature</h2>
            {session.session_feedback ? (
              <div className="space-y-4">
                {[
                  { key: 'effective_strategies', label: 'Most Effective Strategies', color: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
                  { key: 'recommendations_next', label: 'Recommendations for Next Session', color: 'bg-blue-50 border-blue-200 text-blue-800' },
                  { key: 'thinking_moment', label: 'Most Prominent Thinking Moment', color: 'bg-violet-50 border-violet-200 text-violet-800' },
                  { key: 'reasoning_strategy', label: 'Strategy to Strengthen Reasoning', color: 'bg-amber-50 border-amber-200 text-amber-800' },
                  { key: 'parent_note', label: 'Parent Communication Note', color: 'bg-cyan-50 border-cyan-200 text-cyan-800' },
                  { key: 'additional_observations', label: 'Additional Observations', color: 'bg-slate-50 border-slate-200 text-slate-700' },
                ].map(({ key, label, color }) => {
                  const val = session.session_feedback[key];
                  if (!val) return null;
                  return (
                    <div key={key} className={`rounded-2xl border p-4 ${color}`}>
                      <p className="text-xs font-bold uppercase tracking-wider opacity-70 mb-1">{label}</p>
                      <p className="text-sm whitespace-pre-wrap">{val}</p>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Instructor</p>
                    <p className="text-sm font-bold text-[#1E1B4B]">{session.session_feedback.instructor_name || 'N/A'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Date</p>
                    <p className="text-sm font-bold text-[#1E1B4B]">{session.session_feedback.date || 'N/A'}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-slate-700 whitespace-pre-wrap">{session.instructor_notes}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Parent Feedback (completed sessions) */}
      {session.status === 'completed' && parentFeedback.length > 0 && (
        <Card className="rounded-3xl border-emerald-100 shadow-sm mb-6 bg-emerald-50/30" data-testid="parent-feedback-section">
          <CardContent className="p-6">
            <h2 className="text-sm font-bold text-emerald-700 uppercase tracking-wider mb-4">Parent Feedback</h2>
            {parentFeedback.map(fb => (
              <div key={fb.feedback_id} className="mb-4 last:mb-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-emerald-600">{fb.parent_name || 'Parent'}</span>
                  <span className="text-[10px] text-slate-400">{new Date(fb.created_at).toLocaleDateString()}</span>
                </div>
                {fb.session_notes && <div className="p-3 bg-white rounded-xl border border-emerald-200 mb-2"><p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">Session Notes</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{fb.session_notes}</p></div>}
                {fb.engagement_observations && <div className="p-3 bg-white rounded-xl border border-blue-200 mb-2"><p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Engagement Observations</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{fb.engagement_observations}</p></div>}
                {fb.suggestions && <div className="p-3 bg-white rounded-xl border border-amber-200"><p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-1">Suggestions</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{fb.suggestions}</p></div>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Visual Schedule */}
      <SessionSkillGrid
        session={session} sessionId={sessionId} activeSkill={activeSkill}
        setActiveSkill={setActiveSkill} isParent={isParent} onFetchSession={fetchSession}
        onOpenFullscreen={(skill) => session.status === 'active' && setFullscreenSkill(skill)}
        onShowImageLibrary={(skillId) => setShowImageLibrary(skillId)}
      />

      {/* Active Skill Actions — instructor only */}
      {!isParent && activeSkill && session.status === 'active' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <SessionVideoActions session={session} sessionId={sessionId} activeSkill={activeSkill} onFetchSession={fetchSession} />
          <SessionComfortRating session={session} sessionId={sessionId} activeSkill={activeSkill} comfortRatings={comfortRatings} onComfortRate={handleComfortRate} />
        </div>
      )}

      {/* Discard Confirmation */}
      <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader><DialogTitle className="text-red-600 flex items-center gap-2" style={{ fontFamily: 'Nunito' }}><Trash2 className="w-5 h-5" /> Discard Session</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">Are you sure you want to discard this session? All recorded videos and data will be permanently deleted.</p>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="rounded-xl font-bold" onClick={() => setShowDiscardConfirm(false)} data-testid="cancel-discard-btn">Cancel</Button>
              <Button className="rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold gap-2" onClick={discardSession} data-testid="confirm-discard-btn"><Trash2 className="w-4 h-4" /> Discard</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* End Session Dialog */}
      <SessionFeedbackForm open={showEndSession} onOpenChange={setShowEndSession} feedback={sessionFeedback} setFeedback={setSessionFeedback} onSubmit={endSession} />

      {/* Fullscreen Skill Overlay */}
      {fullscreenSkill && (
        <div className="fixed inset-0 z-50 bg-[#1E1B4B] flex flex-col items-center justify-center" data-testid="fullscreen-skill-overlay">
          <button onClick={() => setFullscreenSkill(null)} className="absolute top-6 right-6 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors z-10" data-testid="fullscreen-close-btn">
            <X className="w-8 h-8 text-white" />
          </button>
          <p className="text-white/60 text-lg font-bold uppercase tracking-widest mb-4">{fullscreenSkill.name}</p>
          <button onClick={async () => { try { await api.put(`/sessions/${sessionId}/skills/${fullscreenSkill.skill_id}/complete`); await fetchSession(); setFullscreenSkill(null); } catch { toast.error('Failed'); } }} className="relative group focus:outline-none" data-testid="fullscreen-complete-btn">
            {getSkillImageSrc(fullscreenSkill) ? (
              <img src={getSkillImageSrc(fullscreenSkill)} alt={fullscreenSkill.name} className="w-72 h-72 sm:w-96 sm:h-96 object-cover rounded-[2rem] border-4 border-white/30 shadow-2xl group-hover:border-[#C4F017] group-active:scale-95 transition-all" />
            ) : (
              <div className="w-72 h-72 sm:w-96 sm:h-96 rounded-[2rem] border-4 border-white/30 bg-white/10 flex items-center justify-center">
                <span className="text-[8rem]">{'🫧🦵🏊🔙💪🌊😮🤸'[session.skills.indexOf(fullscreenSkill) % 8]}</span>
              </div>
            )}
            <div className="absolute inset-0 rounded-[2rem] bg-[#C4F017]/0 group-hover:bg-[#C4F017]/10 group-active:bg-[#C4F017]/20 transition-colors flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity"><CheckCircle2 className="w-20 h-20 text-[#C4F017] drop-shadow-lg" /></div>
            </div>
          </button>
          <p className="text-white/40 text-sm mt-6 font-semibold">Tap the image to complete this skill</p>
        </div>
      )}

      {/* Image Library Dialog */}
      <SessionImageLibrary
        open={!!showImageLibrary} onOpenChange={() => setShowImageLibrary(null)}
        customLibrary={customLibrary} presetLibrary={SKILL_IMAGE_LIBRARY}
        onSelectImage={(url) => showImageLibrary && handlePresetImage(showImageLibrary, url)}
        onDeleteFromLibrary={deleteFromLibrary} onSavePreset={savePresetToLibrary}
        onUploadToLibrary={uploadToLibrary} uploadingToLibrary={uploadingToLibrary}
        newImageName={newImageName} setNewImageName={setNewImageName}
        getLibraryImageSrc={getLibraryImageSrc}
      />
    </div>
  );
}


function RewardEditor({ session, isInstructor, allComplete, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [ifCond, setIfCond] = useState(session.if_condition || '');
  const [thenReward, setThenReward] = useState(session.then_reward || '');
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);

  const rewardImgSrc = session.reward_image
    ? (session.reward_image.startsWith('http') ? session.reward_image : `${BACKEND_URL}/api/uploads/${session.reward_image}`)
    : null;

  const handleSave = async () => {
    setSaving(true);
    try { await api.put(`/sessions/${session.session_id}`, { if_condition: ifCond, then_reward: thenReward }); toast.success('Reward updated'); setEditing(false); onUpdate(); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/sessions/${session.session_id}/reward-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Reward image uploaded');
      onUpdate();
    } catch { toast.error('Failed to upload image'); }
    finally { setUploadingImg(false); }
  };

  if (editing) {
    return (
      <div className="bg-gradient-to-r from-[#ECFCCB] to-[#F7FEE7] rounded-3xl p-6 mb-6" data-testid="if-then-reward-edit">
        <div className="space-y-3">
          <div>
            <label className="text-sm font-bold text-lime-700 uppercase tracking-wider">IF</label>
            <input value={ifCond} onChange={e => setIfCond(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-xl bg-white border text-sm" placeholder="I complete all skills..." data-testid="edit-if-condition" />
          </div>
          <div>
            <label className="text-sm font-bold text-lime-700 uppercase tracking-wider">THEN</label>
            <input value={thenReward} onChange={e => setThenReward(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-xl bg-white border text-sm" placeholder="I earn a reward!" data-testid="edit-then-reward" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2" data-testid="save-reward-btn">
              <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outline" onClick={() => setEditing(false)} className="rounded-full">Cancel</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-[#ECFCCB] to-[#F7FEE7] rounded-3xl p-6 mb-6" data-testid="if-then-reward">
      <div className="flex flex-col sm:flex-row items-center gap-4">
        {rewardImgSrc && <img src={rewardImgSrc} alt="Reward" className="w-20 h-20 rounded-2xl object-cover border-2 border-[#C4F017] shadow-md" data-testid="reward-image-preview" />}
        <div className="flex-1 text-center sm:text-left">
          <p className="text-sm font-bold text-lime-700 uppercase tracking-wider mb-1">IF</p>
          <p className="text-lg font-bold text-[#1E1B4B]" style={{ fontFamily: 'Nunito' }}>{session.if_condition || 'I complete all skills'}</p>
        </div>
        <ArrowRight className="w-8 h-8 text-[#C4F017] flex-shrink-0" />
        <div className="flex-1 text-center sm:text-right">
          <p className="text-sm font-bold text-lime-700 uppercase tracking-wider mb-1">THEN</p>
          <p className="text-lg font-bold text-[#1E1B4B]" style={{ fontFamily: 'Nunito' }}>{session.then_reward || 'I earn a reward!'}</p>
        </div>
        {allComplete && <Award className="w-12 h-12 text-[#C4F017] animate-starBounce flex-shrink-0" />}
      </div>
      {isInstructor && session.status !== 'completed' && (
        <div className="flex gap-2 mt-4 justify-end">
          <label className="cursor-pointer">
            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
            <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${uploadingImg ? 'bg-slate-300 text-slate-500' : 'bg-white/80 text-lime-700 hover:bg-white'}`}>
              <ImagePlus className="w-3.5 h-3.5" /> {uploadingImg ? 'Uploading...' : 'Reward Image'}
            </div>
          </label>
          <button onClick={() => { setIfCond(session.if_condition || ''); setThenReward(session.then_reward || ''); setEditing(true); }} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-white/80 text-lime-700 hover:bg-white" data-testid="edit-reward-btn">
            <Edit3 className="w-3.5 h-3.5" /> Edit
          </button>
        </div>
      )}
    </div>
  );
}
